import React, { useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { IconButton } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";

export const GeoLocationProps = {
  geographyType: "Zip code or State",
  disabled: false,
  onChange: () => {},
  onEnter: () => {},
  onClear: () => {},
  locationLabel: "Location",
  errorMessage: "",
  states: [],
  highlight: false,
  partName: "pfLocation",
  classNamePrefix: "norstella-pf",
  identifier: "",
  hideLocation: false
};

/**
 * Reusable geographic location input supporting three modes based on geographyType (provided via props):
 * - "zip code only": free text ZIP input
 * - "state only": dropdown of states (provided via props)
 * - "zip code or state": free text allowing ZIP or state, with optional state options for assist
 */
const GeoLocationInput = ({ geoLocationProps = GeoLocationProps }) => {
  const geoSearchRef = useRef();
  const [geoLocationValue, setGeoLocationValue] = useState("");
  const [highlightedOption, setHighlightedOption] = useState(null);
  const isZipCodeOrState = geoLocationProps.geographyType.toLowerCase() === "zip code or state";
  const isStateOnly = !isZipCodeOrState && geoLocationProps.geographyType?.toLowerCase() === "state only";
  const locationPlaceholder = isZipCodeOrState
    ? "Enter ZIP Code or State"
    : isStateOnly
      ? "Enter State"
      : "Enter ZIP Code";

  const handleInput = (event) => {
    const inputValue = event.target.value;
    
    if (isStateOnly) {
      // For state only: allow only alphabets and spaces
      if (!/^[a-zA-Z\s]*$/.test(inputValue)) {
        event.target.value = inputValue.replace(/[^a-zA-Z\s]/g, '');
        setGeoLocationValue(event.target.value);
      }
    } else if (isZipCodeOrState) {
      // For "zip code or state": allow alphabets, digits, and spaces
      if (!/^[a-zA-Z0-9\s]*$/.test(inputValue)) {
        event.target.value = inputValue.replace(/[^a-zA-Z0-9\s]/g, '');
        setGeoLocationValue(event.target.value);
      }
    } else {
      // For zip code only: allow only digits, max 5 digits, no spaces
      if (!/^\d*$/.test(inputValue) || inputValue.length > 5) {
        const validDigits = inputValue.replace(/\D/g, '').slice(0, 5);
        event.target.value = validDigits;
        setGeoLocationValue(validDigits);
      }
    }
  };

  const handleInputChange = (newInputValue) => {
    if (newInputValue && newInputValue.trim().length > 0) {
      setGeoLocationValue(newInputValue);
      if (geoLocationValue !== newInputValue) {
        geoLocationProps.onChange(newInputValue.trim());
      }
      return;
    }
    handleClear();
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13 && event.target.value) {
      if ((isZipCodeOrState || isStateOnly) && highlightedOption) {
        const optionText = typeof highlightedOption === 'string' ? highlightedOption : highlightedOption?.name || '';
        if (optionText) {
          setGeoLocationValue(optionText);
          geoLocationProps.onEnter();
          return;
        }
      }      
      // Fallback to using typed text (for zip code only or when no option is highlighted)
      setGeoLocationValue(event.target.value);
      geoLocationProps.onEnter();
      if (!isNaN(Number(event.target.value[0]))) {
        event.stopPropagation();
      }
    }
  };

  const handleClear = () => {
    setGeoLocationValue("");
    geoLocationProps.onClear();
  };
  
  const compareOptionWithGeoLocationValue = (option, value) => {
    if (typeof option === 'string' && typeof value === 'string') {
      return option === value;
    }
    if (typeof option === 'object' && option?.name && typeof value === 'string') {
      return option.name === value;
    }
    if (typeof option === 'object' && typeof value === 'object') {
      return option?.name === value?.name;
    }
    return false;
  }

  return (
    !geoLocationProps.hideLocation && (
    <div className={`${geoLocationProps.classNamePrefix}-location-controls ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-location-controls` : ''}`}>
      <label className={`${geoLocationProps.classNamePrefix}-search-label ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-search-label` : ''}`}>{geoLocationProps.locationLabel}</label>
      <div
        className={`${geoLocationProps.classNamePrefix}-location-input-row ${geoLocationProps.classNamePrefix}-input-group ${geoLocationProps.highlight && !geoLocationProps.errorMessage ? `${geoLocationProps.classNamePrefix}-highlight-search` : ""} ${geoLocationProps.errorMessage ? `${geoLocationProps.classNamePrefix}-error-state` : ""} ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-location-input-row` : ''}`}
        part={`${geoLocationProps.partName}Div`}
      >
        <Autocomplete
          id="geoLocationInput"
          className={`${geoLocationProps.classNamePrefix}-geo-autocomplete ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-geo-autocomplete` : ''}`}
          part={`${geoLocationProps.partName}Autocomplete`}
          disablePortal
          freeSolo={!isStateOnly}
          {...(isStateOnly ? {} : { popupIcon: null })}
          blurOnSelect
          handleHomeEndKeys
          options={isZipCodeOrState || isStateOnly ? geoLocationProps.states : []}
          value={geoLocationValue}
          disabled={geoLocationProps.disabled}
          onInputChange={(_, value) => handleInputChange(value)}
          onChange={(_, selectedOption) => {
            if (selectedOption) {
              // When a state is selected from dropdown, set it as the value
              const selectedValue = typeof selectedOption === 'string' ? selectedOption : selectedOption?.name || '';
              setGeoLocationValue(selectedValue);
              geoLocationProps.onChange(selectedValue.trim());
            } else {
              handleClear();
            }
          }}
          onHighlightChange={(_, highlightedOption) => {
            setHighlightedOption(highlightedOption);
          }}
          ref={geoSearchRef}
          noOptionsText="No states"
          isOptionEqualToValue={compareOptionWithGeoLocationValue}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              placeholder={locationPlaceholder}
              className={`${geoLocationProps.classNamePrefix}-input ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-input` : ''}`}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {geoLocationValue && geoLocationValue.length > 0 && !geoLocationProps.disabled ? (
                      <IconButton
                        className={`${geoLocationProps.classNamePrefix}-autocomplete-custom-close-location ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-autocomplete-custom-close-location` : ''}`}
                        style={{ marginRight: !isStateOnly ? "0" : "15px" }}
                        tabIndex={0}
                        aria-label="Clear button"
                        onClick={handleClear}
                      >
                        <ClearIcon />
                      </IconButton>
                    ) : null}
                  </>
                ),
              }}
            />
          )}
          getOptionLabel={(option) => (typeof option === "string" ? option : option?.name ?? option?.value ?? "")}
        />
      </div>
      {geoLocationProps.errorMessage && (
        <div className={`${geoLocationProps.classNamePrefix}-error-message ${geoLocationProps.identifier ? `${geoLocationProps.classNamePrefix}-${geoLocationProps.identifier}-error-message` : ''}`} part={`${geoLocationProps.partName}ErrorMessage`}>
          {geoLocationProps.errorMessage}
        </div>
      )}
    </div>)
  );
};

export default GeoLocationInput;
